"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { handleMultiSelect } from "./utils";
import type { AnalyticsFilters } from "./types";

type FiltersPanelProps = {
  filters: AnalyticsFilters;
  setFilters: (next: AnalyticsFilters) => void;
  creditorRegionOptions: string[];
  borrowerRegionOptions: string[];
  creditorTypeColumns: string[];
  denominationColumns: string[];
  minYear: number;
  maxYear: number;
  sticky?: boolean;
  horizontal?: boolean;
  /** Skip the Card/CardContent wrapper (renders inline) */
  noCard?: boolean;
};

export default function FiltersPanel({
  filters,
  setFilters,
  creditorRegionOptions,
  borrowerRegionOptions,
  creditorTypeColumns,
  denominationColumns,
  minYear,
  maxYear,
  sticky = true,
  horizontal = false,
  noCard = false,
}: FiltersPanelProps) {
  const creditorRegionSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Creditor Region</InputLabel>
      <Select
        multiple
        value={filters.selectedCreditorRegions}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCreditorRegions: next }),
          )
        }
        input={<OutlinedInput label="Creditor Region" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {creditorRegionOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedCreditorRegions.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const borrowerRegionSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Borrower Region</InputLabel>
      <Select
        multiple
        value={filters.selectedBorrowerRegions}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedBorrowerRegions: next }),
          )
        }
        input={<OutlinedInput label="Borrower Region" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {borrowerRegionOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedBorrowerRegions.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const creditorTypeSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Creditor Type</InputLabel>
      <Select
        multiple
        value={filters.selectedCreditorTypes}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCreditorTypes: next }),
          )
        }
        input={<OutlinedInput label="Creditor Type" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {creditorTypeColumns.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedCreditorTypes.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const denominationSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Denomination</InputLabel>
      <Select
        multiple
        value={filters.selectedDenominations}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedDenominations: next }),
          )
        }
        input={<OutlinedInput label="Denomination" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {denominationColumns.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedDenominations.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeSlider = (
    <Box sx={horizontal ? { width: "300px" } : {}}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0 }}>
        Date Range: {filters.yearRange[0]} - {filters.yearRange[1]}
      </Typography>
      <Slider
        value={filters.yearRange}
        min={minYear}
        max={maxYear}
        onChange={(_, value) =>
          setFilters({ ...filters, yearRange: value as number[] })
        }
        valueLabelDisplay="auto"
      />
    </Box>
  );

  const completeDataToggle = (
    <Box
      sx={
        horizontal
          ? {
              display: "flex",
              alignItems: "center",
              gap: 1,
              minHeight: 40,
              marginLeft: "2rem",
            }
          : {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }
      }
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Complete Metadata Only
      </Typography>
      <Switch
        checked={filters.completeDataOnly}
        onChange={(event) =>
          setFilters({ ...filters, completeDataOnly: event.target.checked })
        }
      />
    </Box>
  );

  const handleReset = () => {
    setFilters({
      selectedCreditorRegions: [],
      selectedBorrowerRegions: [],
      selectedCreditorTypes: [],
      selectedDenominations: [],
      yearRange: [minYear, maxYear],
      completeDataOnly: false,
    });
  };

  const resetButton = (
    <Button
      variant="outlined"
      // variant="contained"
      size="small"
      onClick={handleReset}
      sx={{ minWidth: 92 }}
    >
      Reset
    </Button>
  );

  const inner = horizontal ? (
    <Stack spacing={2.2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        {creditorRegionSelect}
        {borrowerRegionSelect}
        {creditorTypeSelect}
        {denominationSelect}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        {dateRangeSlider}
        {completeDataToggle}
        {resetButton}
      </Box>
    </Stack>
  ) : (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Filters
      </Typography>
      <Stack spacing={2.2}>
        {creditorRegionSelect}
        {borrowerRegionSelect}
        {creditorTypeSelect}
        {denominationSelect}
        {dateRangeSlider}
        {completeDataToggle}
        {resetButton}
      </Stack>
    </>
  );

  if (noCard) {
    return inner;
  }

  return (
    <Card
      sx={{
        height: "fit-content",
        position: sticky ? { lg: "sticky" } : "static",
        top: sticky ? { lg: 16 } : "auto",
      }}
    >
      <CardContent>{inner}</CardContent>
    </Card>
  );
}
